// src/context/BreadcrumbContext.jsx
//
// Breadcrumbs are built from the URL (where the page sits), not from click
// history. URL segments that are record ids (/equipment/<uuid>) are shown by
// name; pages report that name here once they have loaded the record:
//
//   useBreadcrumbLabel(equipmentId, equipment?.name);
//
// PageHeader does this automatically for detail pages whose title is the
// record name. Names are kept for the session, so list → detail → edit keeps
// showing the same crumb without waiting for a reload.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const BreadcrumbContext = createContext(null);

export function BreadcrumbProvider({ children }) {
  const [labels, setLabels] = useState({});

  const setLabel = useCallback((id, label) => {
    setLabels((prev) => (prev[id] === label ? prev : { ...prev, [id]: label }));
  }, []);

  const value = useMemo(() => ({ labels, setLabel }), [labels, setLabel]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** Name the breadcrumb for record `id` (a URL segment). No-op outside the provider. */
export function useBreadcrumbLabel(id, label) {
  const setLabel = useContext(BreadcrumbContext)?.setLabel;
  useEffect(() => {
    if (setLabel && id && typeof label === 'string' && label.trim()) setLabel(String(id), label.trim());
  }, [setLabel, id, label]);
}

export function useBreadcrumbLabels() {
  return useContext(BreadcrumbContext)?.labels ?? {};
}
